/**
 * Authenticated binary download helper. The regular `api.ts` transport only
 * handles JSON responses; PDF / CSV exports need the raw blob but should
 * still participate in the silent-refresh-on-401 flow.
 */
import { tokenManager } from './tokenManager'

async function fetchWithAuth(url: string): Promise<Response> {
  const build = (): RequestInit => {
    const token = tokenManager.get()
    return {
      credentials: 'same-origin',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  }
  let res = await fetch(url, build())
  if (res.status === 401) {
    const ok = await tokenManager.refreshNow()
    if (ok) res = await fetch(url, build())
  }
  return res
}

/**
 * Fetch `url` and trigger a browser download of the response body. Returns
 * nothing on success; throws an Error with a useful message on failure.
 */
export async function downloadAuthenticatedFile(
  url: string,
  filename: string,
): Promise<void> {
  const res = await fetchWithAuth(url)
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    const ct = res.headers.get('content-type') || ''
    if (ct.includes('application/json')) {
      try {
        const body = await res.json()
        if (body?.error) detail = String(body.error)
        else if (body?.detail) detail = String(body.detail)
      } catch {
        /* ignore */
      }
    }
    throw new Error(detail)
  }
  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}
